import clsx from 'clsx';
import { Layout } from '../../components/Common/Layout';
import useBreakpoints from '../../hooks/useBreakpoints';
import spouse from '../../assets/spouse.svg';
import kid from '../../assets/kid.svg';
import kidComplete from '../../assets/avatar/kid-avatar.png';
import wife from '../../assets/avatar/female-spouse-avatar.png';
import husband from '../../assets/avatar/male-spouse-avatar.png';
import calendar from '../../assets/calendar.svg';
import {
  ArrowRight,
  AvatarMale,
  HourGlassWithLetter,
  InfoIcon,
  SadPumpkin,
} from 'ui/icons';
import Divider from 'ui/ds/Divider';
import { Button } from 'ui/mobile-ds';
import { PageHeader } from '../../components/Common/Layout/Header/PageHeader';
import useModalRoute from '../../hooks/useModalRoute';
import getDimensions from '../../utils/getDimensions';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '../../components/Common/Layout/Loader';
import { useEffect, useState } from 'react';
import {
  addFamilyMembersQuery,
  memberWithDependentsQuery,
} from '@/queries/add-family-member.queries';
import { EMemberRelationship, EndorsementMemberStatusEnum } from '@/types';
import AddMemberForm from '@/components/Common/AddFamilyMember/AddMemberForm';
import RequestHistory from '@/components/Common/AddFamilyMember/RequestHistory';
import FamilyCard from '@/components/Common/AddFamilyMember/FamilyCard';
import AddFamilyMemberErrorScreen from '@/components/Common/AddFamilyMember/AddFamilyMemberErrorScreen';
import AdditionNotAllowed from '@/components/Common/AddFamilyMember/AdditionNotAllowed';
import { useAppContext } from '@/context/AppContext';
import { redirectToPlumApp, track } from '@/utils';
import { useNavigate } from 'react-router-dom';
import { useRelationContext } from '@/context/RelationContext';
import PendingStateScreen from '@/components/Common/AddFamilyMember/PendingStateScreen';
import { calculateDaysFromToday } from '@/utils/calculateDaysFromToday';
import { TRACKER_EVENTS } from '@/constants';

const AddFamilyMembers = () => {
  const { isWebView } = useAppContext();
  const navigate = useNavigate();
  const {
    data: familyMembersData,
    isLoading: isFamilyMembersLoading,
    isError: isFamilyMembersError,
    refetch: addFamilyMembersQueryRefetch,
    isFetched: isAddFamilyMembersQueryFetched,
  } = useQuery({
    queryKey: ['addFamilyMembers'], // Unique key for the query
    queryFn: addFamilyMembersQuery().queryFn, // Call the query function
  });

  const {
    data: memberWithDependentsData,
    isLoading: isMemberWithDependentsLoading,
    isError: isMemberWithDependentError,
    refetch: memberWithDependentsQueryRefetch,
    isFetched: isMemberWithDependentFetched,
  } = useQuery({
    queryKey: ['requestHistory'], // Unique key for the query
    queryFn: memberWithDependentsQuery().queryFn, // Call the query function
  });

  const spouseCompleteIcon =
    memberWithDependentsData?.gender === 'MALE' ? wife : husband;
  const { isMobile } = useBreakpoints();
  const { isOpen: isAddFormOpen, onOpenChange: onOpenAddFormChange } =
    useModalRoute({
      additions: {
        afmf: 'true',
      },
    });
  const {
    isOpen: isRequestHistoryOpen,
    onOpenChange: onOpenRequestHistoryChange,
  } = useModalRoute({
    additions: {
      rh: 'true',
    },
  });
  const [relation, setRelation] = useState<string>('');

  const { relationData } = useRelationContext(); // Ensure you have access to relationData

  useEffect(() => {
    if (relationData?.relationship) {
      if (
        relationData.relationship === 'Son' ||
        relationData.relationship === 'Daughter'
      ) {
        setRelation(EMemberRelationship.CHILD);
      } else if (
        relationData.relationship === 'Husband' ||
        relationData.relationship === 'Wife'
      ) {
        setRelation(EMemberRelationship.SPOUSE);
      }
      onOpenAddFormChange(true); // Open the AddMemberForm
    } else {
      addFamilyMembersQueryRefetch();
      memberWithDependentsQueryRefetch();
      onOpenAddFormChange(false);
    }
  }, [relationData]);

  const isPageLoading =
    isMemberWithDependentsLoading ||
    isFamilyMembersLoading ||
    !isAddFamilyMembersQueryFetched ||
    !isMemberWithDependentFetched;

  useEffect(() => {
    if (
      !isPageLoading &&
      isAddFamilyMembersQueryFetched &&
      isMemberWithDependentFetched
    ) {
      track({
        event: TRACKER_EVENTS.MID_TERM_ADDITION_PAGE_LOADED,
      });
    }
    if (isWebView && !isPageLoading) {
      window?.NativeBridge?.onLoadComplete?.();
    }
  }, [isWebView, isPageLoading]);

  const handleAddMemberFormOpen = (value: string) => {
    track({
      event: TRACKER_EVENTS.MID_TERM_ADDITION_REQUEST_STARTED,
      data: { relationship: value },
    });
    setRelation(value);
    onOpenAddFormChange(true);
  };

  const handleSeeAllRequestButton = () => {
    onOpenRequestHistoryChange(true);
  };

  const isAllowedSpouseCountZero =
    familyMembersData?.relationshipValidation?.some(
      (relation: any) =>
        relation.relationshipType === EMemberRelationship.SPOUSE &&
        relation.validations.allowed === 0,
    );

  const isAllowedChildrenCountZero =
    familyMembersData?.relationshipValidation?.some(
      (relation: any) =>
        relation.relationshipType === EMemberRelationship.CHILD &&
        relation.validations.allowed === 0,
    );

  const isSpouseCanBeAdded = familyMembersData?.relationshipValidation?.some(
    (relation: any) =>
      relation.relationshipType === EMemberRelationship.SPOUSE &&
      relation.validations.available > 0,
  );

  const isChildCanBeAdded = familyMembersData?.relationshipValidation?.some(
    (relation: any) =>
      relation.relationshipType === EMemberRelationship.CHILD &&
      relation.validations.available > 0,
  );

  const isChildPending = memberWithDependentsData?.dependents?.some(
    (item: any) =>
      item.relationship === EMemberRelationship.CHILD &&
      item.endorsementStatus === EndorsementMemberStatusEnum.Pending,
  );

  const isSpousePending = memberWithDependentsData?.dependents?.some(
    (item: any) =>
      item.relationship === EMemberRelationship.SPOUSE &&
      item.endorsementStatus === EndorsementMemberStatusEnum.Pending,
  );

  const isPageApiLoadFails = isFamilyMembersError || isMemberWithDependentError;

  const handleErrorScreenClick = () => {
    navigate('/family-addition/add-family-member');
  };

  const isEmployeeOnlyPolicy =
    isAllowedChildrenCountZero && isAllowedSpouseCountZero;

  const isGMCNotAvailable = familyMembersData?.length === 0;

  const handleBack = () => {
    if (isWebView) {
      window?.NativeBridge?.onNativeClose?.();
    } else {
      redirectToPlumApp('/benefits-revamp')();
    }
  };

  const backDateForDOB = familyMembersData?.relationshipValidation?.find(
    (member: any) => member.relationshipType === EMemberRelationship.CHILD,
  )?.validations.backDateForMarriageDate;

  const backDateForMarriageDate =
    familyMembersData?.relationshipValidation?.find(
      (member: any) => member.relationshipType === EMemberRelationship.SPOUSE,
    )?.validations.backDateForMarriageDate;

  return (
    <>
      <Layout.Main
        fullWidth
        hideFooter
        rootClassName="gap-3"
        containerProps={{
          className: clsx({ 'h-full max-h-full !m-0': true }),
        }}
        className="p-0"
        headerProps={{
          hidePageHeader: true,
          sticky: true,
        }}
      >
        {({ headerDimensions, gap, bg }) => {
          const backBarDimensions = getDimensions('claims-back-bar');
          return (
            <>
              {isPageLoading ? (
                <Loader />
              ) : (
                <PageHeader
                  fullWidth={true}
                  bg={bg}
                  title={`Add family members`}
                  forceRenderTitleInBackBar={Boolean(isMobile)}
                  renderTitleInBackBar={Boolean(isMobile)}
                  backBarClassName={clsx({
                    'sticky z-20 w-full': true,
                  })}
                  backBarStyles={{
                    top: isMobile
                      ? headerDimensions.height
                      : headerDimensions.height + gap,
                  }}
                  renderOrder={['title', 'children']}
                  className={clsx({
                    '!items-start lg:mt-8 h-full': true,
                  })}
                  titleClassName="h-full"
                  childrenContainerClassName="gap-y-0 sm:pt-3 max-w-[824px] w-full mx-auto h-full"
                  onBack={handleBack}
                >
                  {isPageApiLoadFails ? (
                    <AddFamilyMemberErrorScreen
                      title={'Failed to load this page'}
                      subtitle={`Don't worry, just try reloading the page`}
                      cta="Reload the page"
                      handleCtaClick={handleErrorScreenClick}
                    />
                  ) : isGMCNotAvailable ? (
                    <AdditionNotAllowed
                      message="Looks like you do not have an active health insurance cover at this moment."
                      icon={<SadPumpkin height={100} width={100} />}
                    />
                  ) : isEmployeeOnlyPolicy ? (
                    <AdditionNotAllowed
                      message="Your policy currently covers only you and not your family members."
                      icon={<AvatarMale height={100} width={100} />}
                    />
                  ) : (
                    <>
                      <AddMemberForm
                        isOpen={isAddFormOpen}
                        onOpenChange={onOpenAddFormChange}
                        relation={relation}
                        backDateForDOB={backDateForDOB}
                        backDateForMarriageDate={backDateForMarriageDate}
                        minimumAge={
                          familyMembersData?.relationshipValidation.find(
                            (member: any) =>
                              member.relationshipType === relation,
                          )?.validations.age.min
                        }
                        maximumAge={
                          familyMembersData?.relationshipValidation.find(
                            (member: any) =>
                              member.relationshipType === relation,
                          )?.validations.age.max
                        }
                      />
                      <RequestHistory
                        isOpen={isRequestHistoryOpen}
                        onOpenChange={onOpenRequestHistoryChange}
                        requestHistoryData={memberWithDependentsData}
                        isRequestHistoryLoading={isMemberWithDependentsLoading}
                      />
                      <div
                        className={clsx({
                          'sticky sm:pt-4 mt-[1px] flex flex-col items-stretch gap-1':
                            true,
                          [`${bg}`]: bg,
                        })}
                        style={{
                          top: isMobile
                            ? headerDimensions.height + backBarDimensions.height
                            : headerDimensions.height + gap + 8 - 40,
                        }}
                      >
                        <div className="flex flex-row mb-6 mt-4 bg-secondary-200 px-4 py-6 rounded-2xl items-start md:items-center gap-2">
                          <InfoIcon width={20} height={20} />
                          <p className="text-sm md:text-base text-secondary-800 font-normal">
                            Family members will be added to your current{' '}
                            <span className="font-bold">
                              group medical cover
                            </span>
                          </p>
                        </div>
                        {(isSpousePending &&
                          !isChildCanBeAdded &&
                          isSpouseCanBeAdded) ||
                        (!isSpouseCanBeAdded &&
                          isChildPending &&
                          !isChildCanBeAdded) ? (
                          <PendingStateScreen
                            icon={<HourGlassWithLetter />}
                            message="You have added all family members. Their details will appear here once approved by the insurer."
                          />
                        ) : (
                          <>
                            {(isSpousePending && !isSpouseCanBeAdded) ||
                            isAllowedSpouseCountZero ? null : (
                              <FamilyCard
                                showCount={false}
                                relation="SPOUSE"
                                avatar={
                                  isSpouseCanBeAdded
                                    ? spouse
                                    : spouseCompleteIcon
                                }
                                helperText={
                                  isSpouseCanBeAdded
                                    ? `Must be added within ${calculateDaysFromToday(
                                        new Date(
                                          backDateForMarriageDate?.year,
                                          backDateForMarriageDate?.monthNumber -
                                            1,
                                          backDateForMarriageDate?.date,
                                        ),
                                      )} days of marriage`
                                    : 'Already added'
                                }
                                handleAddMemberFormOpen={
                                  handleAddMemberFormOpen
                                }
                                isRelationCanBeAdded={isSpouseCanBeAdded}
                              />
                            )}
                            <div className="mt-4" />
                            {(isChildPending && !isChildCanBeAdded) ||
                            isAllowedChildrenCountZero ? null : (
                              <FamilyCard
                                showCount={true}
                                relation="CHILD"
                                avatar={isChildCanBeAdded ? kid : kidComplete}
                                helperText={
                                  isChildCanBeAdded
                                    ? `Must be added within ${calculateDaysFromToday(
                                        new Date(
                                          backDateForDOB?.year,
                                          backDateForDOB?.monthNumber - 1,
                                          backDateForDOB?.date,
                                        ),
                                      )} days of birth`
                                    : 'Maximum children added'
                                }
                                handleAddMemberFormOpen={
                                  handleAddMemberFormOpen
                                }
                                isRelationCanBeAdded={isChildCanBeAdded}
                                allowedChildrenCount={
                                  familyMembersData?.relationshipValidation.find(
                                    (relation: any) =>
                                      relation.relationshipType ===
                                      EMemberRelationship.CHILD,
                                  )?.validations.allowed
                                }
                                availableChildrenCount={
                                  familyMembersData?.relationshipValidation.find(
                                    (relation: any) =>
                                      relation.relationshipType ===
                                      EMemberRelationship.CHILD,
                                  )?.validations.available
                                }
                              />
                            )}
                            <div className="mt-6" />
                            <Divider className="border-mobile-gray-200" />
                            <div className="mb-6" />
                            <div className="bg-beach-50 rounded-2xl p-6 flex flex-row gap-2 items-center">
                              <img src={calendar} />
                              <p className="text-base text-secondary-900 font-normal ml-[10px]">
                                You cannot add your{' '}
                                <span className="font-bold">parents</span> or{' '}
                                <span className="font-bold">in-laws</span> to
                                your health insurance policy mid-term, as it is
                                not permitted by the insurer.
                              </p>
                            </div>
                          </>
                        )}
                        <div className="flex justify-start md:justify-end">
                          <Button
                            intent="tertiary"
                            className="flex gap-2 items-center text-sm md:text-base font-medium"
                            onClick={handleSeeAllRequestButton}
                          >
                            See all requests{' '}
                            <ArrowRight className="w-4 h-4 md:w-auto md:h-auto" />
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </PageHeader>
              )}
            </>
          );
        }}
      </Layout.Main>
    </>
  );
};

export default AddFamilyMembers;
